import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Users, Video, DollarSign, ShoppingCart, Sparkles, Swords, TrendingUp } from "lucide-react";
import { SIWorkflows } from "@/components/admin/SIWorkflows";
import { Agreements } from "@/components/admin/Agreements";
import { Schedule } from "@/components/admin/Schedule";
import { ContentStudio } from "@/components/admin/ContentStudio";

export default function Admin() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => { if (!loading && (!user || user.role !== "admin")) navigate("/"); }, [loading, user, navigate]);
    if (loading || !user || user.role !== "admin")
        return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-teal" /></div>;

    return (
        <div className="min-h-screen bg-[#F6F9FD]">
            <Header onTrial={() => {}} onLogin={() => {}} />
            <main className="mx-auto max-w-6xl px-4 pt-28 pb-20 lg:px-8">
                <div className="mb-8">
                    <div className="section-label mb-2">Owner Command Center</div>
                    <h1 className="font-heading text-3xl font-bold text-navy sm:text-4xl">Admin Dashboard</h1>
                    <p className="mt-2 text-slate-500">Your leads, signups, revenue, and competitive edge — all in one place.</p>
                </div>
                <Tabs defaultValue="si">
                    <TabsList className="mb-8 flex-wrap rounded-full border border-slate-200 bg-white p-1 shadow-sm">
                        {[["si", "SI Workflows"], ["studio", "Content Studio"], ["schedule", "Schedule"], ["agreements", "Agreements"], ["overview", "Overview"], ["inquiries", "Inquiries"], ["leads", "Leads"], ["webinar", "Webinar"], ["orders", "Orders"], ["intel", "Competitor Intel"]].map(([v, l]) => (
                            <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="data-[state=active]:bg-navy data-[state=active]:text-white">{l}</TabsTrigger>
                        ))}
                    </TabsList>
                    <TabsContent value="studio"><ContentStudio /></TabsContent>
                    <TabsContent value="schedule"><Schedule /></TabsContent>
                    <TabsContent value="si"><SIWorkflows /></TabsContent>
                    <TabsContent value="agreements"><Agreements /></TabsContent>
                    <TabsContent value="overview"><Overview /></TabsContent>
                    <TabsContent value="inquiries"><Inquiries /></TabsContent>
                    <TabsContent value="leads"><Leads /></TabsContent>
                    <TabsContent value="webinar"><Webinars /></TabsContent>
                    <TabsContent value="orders"><Orders /></TabsContent>
                    <TabsContent value="intel"><Intel /></TabsContent>
                </Tabs>
            </main>
        </div>
    );
}

function Overview() {
    const [s, setS] = useState(null);
    useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)).catch(() => {}); }, []);
    const cards = s ? [
        { icon: DollarSign, label: "Revenue (paid)", value: money(Math.round(s.revenue * 100)), color: "text-teal" },
        { icon: ShoppingCart, label: "Paid orders", value: s.paid_orders, color: "text-amber-600" },
        { icon: Users, label: "$1 Trial leads", value: s.leads, color: "text-teal" },
        { icon: Video, label: "Webinar signups", value: s.webinar_regs, color: "text-amber-600" },
    ] : [];
    if (!s) return <Spinner />;
    return (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-overview">
            {cards.map((c) => (
                <div key={c.label} className="glass-card rounded-2xl p-6">
                    <c.icon className={`h-6 w-6 ${c.color}`} />
                    <div className="mt-4 font-heading text-3xl font-extrabold text-navy">{c.value}</div>
                    <div className="mt-1 text-sm text-slate-500">{c.label}</div>
                </div>
            ))}
        </div>
    );
}

function TableWrap({ head, children, testid }) {
    return (
        <div className="glass-card overflow-hidden rounded-2xl" data-testid={testid}>
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                        <tr>{head.map((h) => <th key={h} className="px-5 py-3 font-semibold">{h}</th>)}</tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">{children}</tbody>
                </table>
            </div>
        </div>
    );
}

const fmt = (d) => { try { return new Date(d).toLocaleString(); } catch { return d; } };
const Empty = ({ cols, msg }) => <tr><td colSpan={cols} className="px-5 py-10 text-center text-slate-500">{msg}</td></tr>;

function Leads() {
    const [rows, setRows] = useState(null);
    useEffect(() => { api.get("/admin/leads").then((r) => setRows(r.data.leads)).catch(() => {}); }, []);
    if (!rows) return <Spinner />;
    return (
        <TableWrap head={["Name", "Email", "Phone", "When"]} testid="admin-leads">
            {rows.length ? rows.map((l, i) => (
                <tr key={i} className="text-slate-700"><td className="px-5 py-3">{l.name}</td><td className="px-5 py-3">{l.email}</td><td className="px-5 py-3">{l.phone || "—"}</td><td className="px-5 py-3 text-slate-500">{fmt(l.created_at)}</td></tr>
            )) : <Empty cols={4} msg="No trial leads yet." />}
        </TableWrap>
    );
}

function Inquiries() {
    const [rows, setRows] = useState(null);
    useEffect(() => { api.get("/admin/inquiries").then((r) => setRows(r.data.inquiries)).catch(() => {}); }, []);
    if (!rows) return <Spinner />;
    return (
        <TableWrap head={["Name", "Email", "Phone", "Interest", "Type", "When"]} testid="admin-inquiries">
            {rows.length ? rows.map((q, i) => (
                <tr key={i} className="text-slate-700">
                    <td className="px-5 py-3">{q.name}</td><td className="px-5 py-3">{q.email}</td><td className="px-5 py-3">{q.phone || "—"}</td>
                    <td className="px-5 py-3">{q.product || "—"}</td><td className="px-5 py-3 text-slate-500">{q.type}</td><td className="px-5 py-3 text-slate-500">{fmt(q.created_at)}</td>
                </tr>
            )) : <Empty cols={6} msg="No inquiries yet." />}
        </TableWrap>
    );
}

function Webinars() {
    const [rows, setRows] = useState(null);
    useEffect(() => { api.get("/admin/webinar-regs").then((r) => setRows(r.data.regs)).catch(() => {}); }, []);
    if (!rows) return <Spinner />;
    return (
        <TableWrap head={["Name", "Email", "Ticket", "When"]} testid="admin-webinar">
            {rows.length ? rows.map((r, i) => (
                <tr key={i} className="text-slate-700"><td className="px-5 py-3">{r.name}</td><td className="px-5 py-3">{r.email}</td><td className="px-5 py-3 font-mono text-teal">{r.ticket}</td><td className="px-5 py-3 text-slate-500">{fmt(r.created_at)}</td></tr>
            )) : <Empty cols={4} msg="No webinar signups yet." />}
        </TableWrap>
    );
}

function Orders() {
    const [rows, setRows] = useState(null);
    useEffect(() => { api.get("/admin/transactions").then((r) => setRows(r.data.transactions)).catch(() => {}); }, []);
    if (!rows) return <Spinner />;
    const color = (s) => s === "paid" ? "bg-teal/15 text-teal" : s === "pending" ? "bg-amber-100 text-amber-600" : "bg-slate-100 text-slate-500";
    return (
        <TableWrap head={["Item", "Amount", "Status", "When"]} testid="admin-orders">
            {rows.length ? rows.map((t, i) => (
                <tr key={i} className="text-slate-700">
                    <td className="px-5 py-3">{t.item_name}</td>
                    <td className="px-5 py-3">{money(t.amount)}</td>
                    <td className="px-5 py-3"><Badge className={`${color(t.payment_status)} hover:${color(t.payment_status)}`}>{t.payment_status}</Badge></td>
                    <td className="px-5 py-3 text-slate-500">{fmt(t.created_at)}</td>
                </tr>
            )) : <Empty cols={4} msg="No transactions yet." />}
        </TableWrap>
    );
}

function Intel() {
    const [data, setData] = useState(null);
    const [insight, setInsight] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => { api.get("/competitors").then((r) => setData(r.data)).catch(() => {}); }, []);

    const generate = async () => {
        setBusy(true);
        try { const { data } = await api.post("/admin/competitor-insight"); setInsight(data.insight); toast.success("AI insight generated"); }
        catch { toast.error("Could not generate insight"); } finally { setBusy(false); }
    };
    if (!data) return <Spinner />;

    return (
        <div className="space-y-6" data-testid="admin-intel">
            <div className="rounded-2xl border border-teal/30 bg-teal/5 p-6">
                <div className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-teal" /><h3 className="font-heading font-bold text-navy">Our positioning</h3></div>
                <p className="mt-2 text-sm text-slate-500">{data.us.model}</p>
                <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                    {data.us.edge.map((e) => <li key={e} className="flex items-start gap-2 text-sm text-slate-700"><Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-teal" /> {e}</li>)}
                </ul>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {data.competitors.map((c) => (
                    <div key={c.name} className="glass-card rounded-2xl p-5">
                        <div className="flex items-center justify-between">
                            <h4 className="font-heading font-bold text-navy">{c.name}</h4>
                            <Badge className="bg-amber-100 text-amber-600 hover:bg-amber-100">★ {c.rating}</Badge>
                        </div>
                        <p className="mt-1 text-xs text-slate-500">{c.model}</p>
                        <p className="mt-2 text-sm font-semibold text-slate-700">{c.pricing}</p>
                        <div className="mt-3 text-xs text-teal">+ {c.strengths.join(", ")}</div>
                        <div className="mt-1 text-xs text-red-600">− {c.weaknesses.join(", ")}</div>
                    </div>
                ))}
            </div>

            <div className="glass-card rounded-2xl p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2"><Swords className="h-5 w-5 text-amber-600" /><h3 className="font-heading font-bold text-navy">AI Positioning Strategy</h3></div>
                    <Button onClick={generate} disabled={busy} data-testid="intel-generate-btn" className="bg-amber-500 font-bold text-white hover:bg-amber-600">
                        {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Analyzing…</> : <><Sparkles className="mr-2 h-4 w-4" /> Generate strategy</>}
                    </Button>
                </div>
                {busy && <p className="mt-3 text-xs text-amber-600">Analyzing your market — this can take up to a minute. Please don't close the tab.</p>}
                {insight ? (
                    <pre className="mt-5 whitespace-pre-wrap rounded-xl border border-slate-200 bg-slate-50 p-5 font-sans text-sm leading-relaxed text-slate-700" data-testid="intel-output">{insight}</pre>
                ) : (
                    !busy && <p className="mt-4 text-sm text-slate-500">Click generate for an AI breakdown of how to beat each competitor on pricing and messaging (takes up to a minute).</p>
                )}
            </div>
        </div>
    );
}

function Spinner() { return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-teal" /></div>; }
